import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL, ENDPOINTS } from '../../../constants/api';
import { getToken } from '../../../utils/TokenUtils';
import '../css/StructureTab.css';

function StructureTab({ table }) {
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCols, setSelectedCols] = useState([]);

  const token = getToken();

  useEffect(() => {
    const fetchStructure = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/${ENDPOINTS.SQL}/structure/${table}`,
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : '',
            },
          }
        );
        setColumns(response.data || []);
      } catch (error) {
        console.error('Error fetching structure:', error);
      } finally {
        setLoading(false);
      }
    };

    if (table) fetchStructure();
  }, [table, token]);

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCols(columns.map((_, index) => index));
    } else {
      setSelectedCols([]);
    }
  };

  const toggleSelectCol = (index) => {
    setSelectedCols((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  if (loading) {
    return <div className="p-3 text-muted">Loading table structure...</div>;
  }

  if (!columns || !columns.length) {
    return (
      <div className="pma-alert-error">
        No column structure found for table <strong>{table}</strong>.
      </div>
    );
  }

  return (
    <div className="pma-structure-wrapper">
      <style>{`
        .pma-structure-wrapper {
          font-family: sans-serif;
          font-size: 12px;
        }
        .pma-structure-title {
          font-size: 14px;
          font-weight: bold;
          color: #333;
          margin-bottom: 12px;
        }
        .pma-struct-table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #d0d0d0;
          background: #ffffff;
        }
        .pma-struct-table th {
          background: linear-gradient(to bottom, #f2f2f2, #e5e5e5);
          border: 1px solid #ccc;
          padding: 6px 8px;
          font-weight: bold;
          color: #333;
          text-align: left;
          white-space: nowrap;
        }
        .pma-struct-table td {
          border: 1px solid #e0e0e0;
          padding: 4px 8px;
          font-family: monospace;
          white-space: nowrap;
        }
        .pma-struct-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        .pma-struct-table tr.selected-row {
          background-color: #fff8c4 !important;
        }
        .pma-struct-table tr:hover {
          background-color: #ffffcc;
        }
        .pma-action-cell {
          font-family: sans-serif;
          font-size: 11px;
          white-space: nowrap;
          text-align: center;
        }
        .pma-action-link {
          color: #23527c;
          text-decoration: none;
          margin: 0 2px;
          cursor: pointer;
        }
        .pma-action-link:hover {
          text-decoration: underline;
        }
        .pma-action-danger {
          color: #a94442;
        }
        .pma-key-badge {
          padding: 1px 4px;
          border-radius: 2px;
          font-size: 10px;
          font-weight: bold;
        }
        .pma-key-pri {
          background-color: #ffcccc;
          color: #990000;
          border: 1px solid #ff9999;
        }
        .pma-key-mul {
          background-color: #e6f2ff;
          color: #0044cc;
          border: 1px solid #b3d7ff;
        }
        .pma-null-tag {
          color: #888;
          font-style: italic;
          background: #eee;
          padding: 1px 4px;
          border-radius: 2px;
        }
        .pma-bottom-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #f5f5f5;
          border: 1px solid #ccc;
          border-top: none;
          padding: 6px 10px;
          border-radius: 0 0 3px 3px;
        }
      `}</style>

      <div className="pma-structure-title">
        Structure of table <span className="font-monospace">`{table}`</span>
      </div>

      <div className="table-responsive">
        <table className="pma-struct-table">
          <thead>
            <tr>
              <th style={{ width: '30px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  onChange={toggleSelectAll}
                  checked={
                    selectedCols.length === columns.length && columns.length > 0
                  }
                />
              </th>
              <th>#</th>
              <th>Field</th>
              <th>Type</th>
              <th>Collation</th>
              <th>Null</th>
              <th>Key</th>
              <th>Default</th>
              <th>Extra</th>
              <th style={{ textAlign: 'center' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((col, index) => {
              const isSelected = selectedCols.includes(index);
              return (
                <tr key={index} className={isSelected ? 'selected-row' : ''}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectCol(index)}
                    />
                  </td>
                  <td>{index + 1}</td>
                  <td className="fw-bold">{col.Field}</td>
                  <td style={{ color: '#0000aa' }}>{col.Type}</td>
                  <td>{col.Collation || '-'}</td>
                  <td>{col.Null === 'YES' ? 'Yes' : 'No'}</td>
                  <td>
                    {col.Key === 'PRI' && (
                      <span className="pma-key-badge pma-key-pri">PRI</span>
                    )}
                    {col.Key === 'MUL' && (
                      <span className="pma-key-badge pma-key-mul">MUL</span>
                    )}
                    {col.Key === 'UNI' && (
                      <span className="pma-key-badge pma-key-mul">UNI</span>
                    )}
                    {!['PRI', 'MUL', 'UNI'].includes(col.Key) &&
                      (col.Key || '-')}
                  </td>
                  <td>
                    {col.Default !== null && col.Default !== undefined ? (
                      col.Default
                    ) : (
                      <span className="pma-null-tag">NULL</span>
                    )}
                  </td>
                  <td>{col.Extra || '-'}</td>
                  <td className="pma-action-cell">
                    <span className="pma-action-link">Change</span> |{' '}
                    <span className="pma-action-link pma-action-danger">
                      Drop
                    </span>{' '}
                    | <span className="pma-action-link">Primary</span> |{' '}
                    <span className="pma-action-link">Unique</span> |{' '}
                    <span className="pma-action-link">Index</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pma-bottom-toolbar">
        <div>
          <span className="text-muted me-2">With selected:</span>
          <button
            className="btn btn-outline-secondary btn-sm py-0 px-2 me-1"
            style={{ fontSize: '11px' }}
          >
            Primary
          </button>
          <button
            className="btn btn-outline-secondary btn-sm py-0 px-2 me-1"
            style={{ fontSize: '11px' }}
          >
            Unique
          </button>
          <button
            className="btn btn-outline-secondary btn-sm py-0 px-2 me-1"
            style={{ fontSize: '11px' }}
          >
            Index
          </button>
          <button
            className="btn btn-outline-danger btn-sm py-0 px-2"
            style={{ fontSize: '11px' }}
          >
            Drop
          </button>
        </div>
        <div className="text-muted">Total columns: {columns.length}</div>
      </div>
    </div>
  );
}

export default StructureTab;
